import { useState, type FormEvent } from 'react';

type Status = 'idle' | 'sending' | 'success' | 'error';

export default function Newsletter() {
  const [status, setStatus] = useState<Status>('idle');
  const [msg, setMsg] = useState('');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const guard = String(fd.get('website_alt') ?? '').trim();
    // 命中蜜罐字段时不提交，展示与正常提交一致的反馈，避免被机器人识别
    if (guard) {
      setStatus('success');
      setMsg('订阅成功！记得查收你的第一份瓜报哦。');
      return;
    }
    if (guard === '') fd.delete('website_alt');

    setStatus('sending');
    setMsg('');
    try {
      const body = new URLSearchParams(fd as unknown as Record<string, string>).toString();
      const res = await fetch('https://readdy.ai/api/form/dagl9n1tto0ceqqnhuh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await res.text();
      let code = '';
      let serverMsg = '';
      try {
        const parsed = JSON.parse(responseText);
        code = parsed?.code ?? '';
        serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      } catch {
        serverMsg = responseText;
      }
      const isSpam = /spam/i.test(responseText) || /spam/i.test(serverMsg);
      if (res.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setMsg('订阅成功！记得查收你的第一份瓜报哦。');
        form.reset();
      } else {
        setStatus('error');
        setMsg(serverMsg || '订阅失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setMsg('网络异常，请稍后再试。');
    }
  }

  return (
    <section id="subscribe" className="scroll-mt-24 bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="relative overflow-hidden rounded-2xl bg-primary-500 px-5 py-10 md:px-12 md:py-14">
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-background-50/10 blur-2xl"
            aria-hidden="true"
          ></div>
          <div
            className="pointer-events-none absolute -bottom-20 left-1/3 h-64 w-64 rounded-full bg-accent-400/30 blur-3xl"
            aria-hidden="true"
          ></div>

          <div className="relative mx-auto grid max-w-5xl gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-3 py-1 text-xs font-semibold text-background-50">
                <i className="ri-mail-send-line" aria-hidden="true"></i>免费订阅 · 随时退订
              </div>
              <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-4xl">
                订阅瓜讯，
                <br />
                第一时间吃上热瓜
              </h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-background-50/85 md:text-base">
                每日一期精选瓜报，浓缩当天最值得吃的爆瓜预警与飙升话题，送到你的邮箱，不刷屏、可退订。
              </p>
            </div>

            <div className="rounded-xl bg-background-50/95 p-4 md:p-5">
              <form
                onSubmit={onSubmit}
                data-readdy-form
                noValidate
                className="flex flex-col gap-3"
              >
                <label htmlFor="subscribe-email" className="text-xs font-semibold text-foreground-700">
                  你的邮箱
                </label>
                <input
                  id="subscribe-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  placeholder="name@example.com"
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
                <input
                  type="text"
                  name="website_alt"
                  data-guard="x"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-lg bg-accent-500 px-5 py-3 text-sm font-semibold text-foreground-950 transition hover:bg-accent-600 disabled:opacity-60"
                >
                  {status === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>提交中…
                    </>
                  ) : (
                    <>
                      立即订阅
                      <i className="ri-send-plane-fill" aria-hidden="true"></i>
                    </>
                  )}
                </button>
                <p className="text-[11px] text-foreground-500">
                  提交即表示你同意接收快瓜资讯邮件，可随时退订。
                </p>

                {status === 'success' && (
                  <p
                    role="status"
                    className="rounded-lg bg-accent-50 px-3 py-2 text-xs font-medium text-accent-800"
                  >
                    <i className="ri-checkbox-circle-fill mr-1" aria-hidden="true"></i>
                    {msg}
                  </p>
                )}
                {status === 'error' && (
                  <p
                    role="alert"
                    className="rounded-lg bg-primary-100 px-3 py-2 text-xs font-medium text-primary-800"
                  >
                    <i className="ri-error-warning-fill mr-1" aria-hidden="true"></i>
                    {msg}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}